import styles from "./BillsPage.module.scss";
import { useTranslation } from "react-i18next";
import Button from "../../components/Button/Button";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import SummaryTile from "../../components/SummaryTile/SummaryTile";
import useOnClickFunctions from "../../components/Drawer/helpers/onClickFunctions";
import RatingComponent from "../../components/RatingComponent/RatingComponent";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import groupDishes from "../../helpers/groupDishes";
import { toppingsTotalGrosze } from "@/helpers/cartPricing";
import TransparentTile from "../../components/TransparentTile/TransparentTile";
import { useLoyaltyPoints } from "@/store/useLoyaltyStore";
import { loyaltyPath, menuPath, orderPaths, tipPath } from "@/routes/paths";
import { useParams } from "react-router-dom";

interface BillsPageProps {
  paid: boolean;
}

function BillsPage(props: BillsPageProps) {
  const { paid } = props;
  const { t, i18n } = useTranslation();
  const { restaurantId = "", tableId = "" } = useParams();

  const {
    paidAmount,
    paidGuests = [],
    remainingAmount,
    remainingGuests = [],
    totalAmount,
    tableNumber,
  } = useVenuesDataStore();

  const { handleNavigate } = useOnClickFunctions();
  const loyaltyPoints = useLoyaltyPoints();

  const ratingTitleThirdPart = t("homePage.rating.title.thirdPart");

  const appRatingLabel = (
    <div className={styles.ratingTitleContainer}>
      <p className={`body-medium ${styles.ratingTitle}`}>
        {t("homePage.rating.title.firstPart")}
      </p>
      <p className={`body-medium-logo ${styles.ratingTitle}`}>
        {t("homePage.footer.title.secondPart")}
      </p>
      {ratingTitleThirdPart ? (
        <p className={`body-medium ${styles.ratingTitle}`}>
          {ratingTitleThirdPart}
        </p>
      ) : null}
    </div>
  );

  const remainingAmountPercentage =
    ((totalAmount - remainingAmount) / totalAmount) * 100;

  const getTranslatedName = (
    translations: { languageCode: string; name: string }[],
  ) => {
    const lang = i18n.language;
    return (
      translations?.find((t) => t.languageCode === lang)?.name ??
      translations?.[0]?.name ??
      ""
    );
  };

  const mapGroupedDishes = (dishes: (typeof remainingGuests)[0]["dishes"]) =>
    groupDishes(dishes).map((dish) => {
      const unitPrice = dish.size?.price ?? dish.price;
      return {
        count: dish.count,
        description: getTranslatedName(dish.translations),
        size: dish.size ? getTranslatedName(dish.size.translations) : undefined,
        extraNote: dish.extraNote,
        basePrice:
          dish.count > 1 || !dish.toppings.lenght
            ? `${(unitPrice / 100).toFixed(2)} zł`
            : undefined,
        price: `${(
          (unitPrice * dish.count +
            toppingsTotalGrosze(dish.toppings) * dish.count) /
          100
        ).toFixed(2)} zł`,
        toppings: dish.toppings?.map(
          (t: {
            translations: { languageCode: string; name: string }[];
            price: number;
            count: number;
          }) => ({
            name: getTranslatedName(t.translations),
            price: `${(t.price / 100).toFixed(2)} zł`,
            count: t.count,
          }),
        ),
      };
    });

  const getGuestTotal = (dishes: (typeof remainingGuests)[0]["dishes"]) => {
    const total = dishes.reduce(
      (sum, dish) =>
        sum +
        (dish.size?.price ?? dish.price) +
        toppingsTotalGrosze(dish.toppings),
      0,
    );
    return `${(total / 100).toFixed(2)} zł`;
  };

  return (
    <div className={styles.billsPageWrapper}>
      <RatingComponent label={appRatingLabel} />

      {/* The account icon carries only a dot, which is easy to miss. This says
          the balance out loud and opens the rewards. */}
      <TransparentTile>
        <div className={styles.loyaltyTileContent}>
          <div className={styles.loyaltyTileHeader}>
            <p className={`body-medium ${styles.loyaltyTileLabel}`}>
              {t("loyalty.balance")}
            </p>
            <p className={`title-large ${styles.loyaltyTileValue}`}>
              {loyaltyPoints}
            </p>
          </div>
          <p className={`body-small ${styles.loyaltyTileHint}`}>
            {loyaltyPoints === 0 ? t("loyalty.empty") : t("loyalty.howItWorks")}
          </p>
          <Button
            onClick={() => {
              handleNavigate(loyaltyPath(restaurantId, tableId));
            }}
            label={t("venuePage.header.loyaltyButtonLabel")}
            icon="las la-award icon-medium"
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
          />
        </div>
      </TransparentTile>

      {/* One group, not three things spaced like sections: these are the
          choices on this screen, so they sit together. */}
      <div className={styles.billsActions}>
        <Button
          onClick={() => {
            handleNavigate(orderPaths(restaurantId, tableId).root);
          }}
          label={t("venuePage.header.orderButtonLabel")}
          icon="las la-utensils icon-medium"
          variant="contained"
          color="primary"
          maxWidth
          mobile={true}
        />
        <Button
          onClick={() => {
            handleNavigate(menuPath(restaurantId));
          }}
          label={t("venuePage.header.menuButtonLabel")}
          icon="las la-book-open icon-medium"
          variant="contained"
          color="primary"
          maxWidth
          mobile={true}
        />
        {/* Thanking one person is its own errand, not a step of paying. */}
        <Button
          onClick={() => {
            handleNavigate(tipPath(restaurantId, tableId));
          }}
          label={t("venuePage.header.tipButtonLabel")}
          icon="las la-hand-holding-usd icon-medium"
          variant="contained"
          color="primary"
          maxWidth
          mobile={true}
        />
      </div>
      <div className={styles.billsSummaryTextContainer}>
        <div className={styles.billsTableSummaryContainer}>
          <p className={`body-bold-medium ${styles.billsTableDescription}`}>
            {t("billsPage.summary.tableDescription")} {tableNumber ?? ""}
          </p>
          <p
            className={`body-medium ${styles.billsSummary} ${
              paid ? styles.billPayed : ""
            }`}
          >
            {`${(totalAmount / 100).toFixed(2)} zł`}
          </p>
        </div>
        <div className={styles.billsPaymentStatusContainer}>
          <p className={`title-large ${styles.billsPaymentStatus}`}>
            {paid
              ? t("billsPage.summary.paidBill")
              : t("billsPage.summary.paymentStatus")}
          </p>
          <p className={`title-large ${styles.billsPaymentStatusAmount}`}>
            {`${(remainingAmount / 100).toFixed(2)} zł`}
          </p>
        </div>
      </div>
      <ProgressBar progress={remainingAmountPercentage} />
      {!paid ? (
        <div className={styles.summaryItemsContainer}>
          {remainingGuests.map((guest) => (
            <SummaryTile
              key={guest.guestNumber}
              guestNumber={guest.guestNumber}
              price={getGuestTotal(guest.dishes)}
              items={mapGroupedDishes(guest.dishes)}
            />
          ))}
        </div>
      ) : (
        <div className={styles.freeToGoContainer}>
          <p className={`body-small ${styles.freeToGoMessage}`}>
            {t("billsPage.freeToGo.title")}
          </p>
          <Button
            label={t("billsPage.freeToGo.billDetailsButton")}
            color="primary"
            onClick={() => {}}
            variant="contained"
          />
        </div>
      )}

      {paidGuests.length > 0 && (
        <div className={styles.paidItemsWrapper}>
          <div className={styles.paidItemsSummaryTitle}>
            <p className={`title-medium ${styles.paidItemsSummaryTitleText}`}>
              {t("billsPage.summary.alreadyPaid")}
            </p>
            <p className={`title-medium ${styles.paidItemsSummaryTitleText}`}>
              {`${(paidAmount / 100).toFixed(2)} zł`}
            </p>
          </div>
          {paidGuests.map((guest) => (
            <SummaryTile
              key={guest.guestNumber}
              guestNumber={guest.guestNumber}
              price={getGuestTotal(guest.dishes)}
              items={mapGroupedDishes(guest.dishes)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default BillsPage;
